<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { RoutePlan } from '@/api/route-plans';
import type { AssignmentsByCaregiver } from '@/composables/useAssignmentsByCaregiver';
import { mergeRouteSteps, type MergedRouteStep } from '@/utils/route-plan-merged-steps';

const { t } = useI18n();

interface Props {
  routePlan: RoutePlan;
  assignmentsByCaregiver: AssignmentsByCaregiver;
  colorMap: Record<number, string>;
  selectedCaregiverId: number | null;
  readOnly?: boolean;
}

const props = withDefaults(defineProps<Props>(), { readOnly: false });
const emit = defineEmits<{
  'select-caregiver': [id: number];
  'update-assignment': [payload: { id: number; status: 'accepted' | 'rejected' }];
}>();

// Caregivers with zero assignments don't appear in assignmentsByCaregiver, which is
// correct: VROOM only generates routes for caregivers with assigned jobs.
const mergedStepsByCaregiver = computed(() => {
  const result: Record<number, MergedRouteStep[]> = {};
  for (const cgIdStr of Object.keys(props.assignmentsByCaregiver)) {
    const cgId = Number(cgIdStr);
    result[cgId] = mergeRouteSteps(
      props.assignmentsByCaregiver[cgId]?.assignments ?? [],
      props.routePlan.caregiverExistingShifts?.[cgId] ?? [],
    );
  }

  return result;
});

const unassignedCaregivers = computed(() => {
  const assignedIds = new Set(Object.keys(props.assignmentsByCaregiver).map(Number));

  return (props.routePlan.routePlanCaregivers || [])
    .filter(rpc => !assignedIds.has(rpc.caregiverId));
});
</script>

<template>
  <v-list density="compact">
    <v-list-group
      v-for="(group, cgId) in assignmentsByCaregiver"
      :key="cgId"
    >
      <template #activator="{ props: activatorProps }">
        <v-list-item
          v-bind="activatorProps"
          :class="{ 'bg-blue-lighten-5': selectedCaregiverId === Number(cgId) }"
          @click.stop="emit('select-caregiver', Number(cgId))"
        >
          <template #prepend>
            <div
              class="rounded-circle mr-2 color-dot-md"
              :style="{ backgroundColor: colorMap[Number(cgId)] }"
            />
          </template>
          <v-list-item-title class="font-weight-bold text-body-2">
            {{ group.name }}
          </v-list-item-title>
          <template #append>
            <v-chip
              size="x-small"
              class="mr-2"
            >
              {{ group.assignments.length }} {{ t('routePlan.visits') }}
            </v-chip>
          </template>
        </v-list-item>
      </template>

      <template
        v-for="step in mergedStepsByCaregiver[Number(cgId)]"
        :key="step.key"
      >
        <!-- Route assignment: chips mark exceptions only (rejected / non-favorite) -->
        <v-list-item
          v-if="step.kind === 'assignment'"
          density="compact"
        >
          <template #prepend>
            <v-avatar
              size="24"
              :color="colorMap[Number(cgId)]"
              class="mr-2"
            >
              <span class="text-white text-caption">{{ step.displaySeq }}</span>
            </v-avatar>
          </template>
          <v-list-item-title class="text-body-2">
            {{ step.assignment.patientFullName }}
          </v-list-item-title>
          <v-list-item-subtitle class="text-caption">
            {{ step.assignment.humanArrivalTime }} - {{ step.assignment.humanDepartureTime }}
            <span
              v-if="step.assignment.humanTravelTime"
              class="text-medium-emphasis"
            >
              · {{ t('routePlan.travelTime') }}: {{ step.assignment.humanTravelTime }}
            </span>
          </v-list-item-subtitle>
          <template #append>
            <v-icon
              v-if="step.assignment.manual"
              icon="mdi-cursor-move"
              size="small"
              color="info"
              class="mr-1"
            >
              <v-tooltip
                activator="parent"
                location="top"
              >
                {{ t('routePlan.manualPlacement') }}
              </v-tooltip>
            </v-icon>
            <v-icon
              v-if="step.assignment.favoriteFallback"
              icon="mdi-star-off-outline"
              size="small"
              color="warning"
              class="mr-1"
            >
              <v-tooltip
                activator="parent"
                location="top"
              >
                {{ t('routePlan.favoriteFallback') }}
              </v-tooltip>
            </v-icon>
            <v-chip
              v-if="step.assignment.status === 'rejected'"
              color="error"
              size="x-small"
              class="mr-1"
            >
              ✗
            </v-chip>
            <v-btn
              v-if="!readOnly && step.assignment.status === 'accepted'"
              size="x-small"
              color="error"
              variant="text"
              icon="mdi-close"
              @click.stop="emit('update-assignment',
                                { id: step.assignment.id, status: 'rejected' })"
            />
            <v-btn
              v-if="!readOnly && step.assignment.status === 'rejected'"
              size="x-small"
              color="success"
              variant="text"
              icon="mdi-undo"
              @click.stop="emit('update-assignment',
                                { id: step.assignment.id, status: 'accepted' })"
            />
          </template>
        </v-list-item>

        <!-- Existing shift -->
        <v-list-item
          v-else
          density="compact"
          class="text-medium-emphasis"
        >
          <template #prepend>
            <v-avatar
              size="24"
              color="grey"
              class="mr-2"
            >
              <span class="text-white text-caption">{{ step.displaySeq }}</span>
            </v-avatar>
          </template>
          <v-list-item-title class="text-body-2">
            {{ step.shift.patientFullName }}
            <v-chip
              size="x-small"
              color="grey"
              variant="tonal"
              class="ml-1"
            >
              {{ t('routePlan.existingShift') }}
            </v-chip>
            <v-chip
              v-if="step.shift.dropped"
              size="x-small"
              color="warning"
              variant="tonal"
              class="ml-1"
            >
              {{ t('routePlan.notOptimized') }}
            </v-chip>
          </v-list-item-title>
          <v-list-item-subtitle class="text-caption">
            {{ step.shift.humanStartTime }} - {{ step.shift.humanEndTime }}
            <span
              v-if="step.shift.rescheduled"
              class="text-warning"
            >
              · {{ t('routePlan.rescheduledFrom', { time: step.shift.originalStartTime }) }}
            </span>
            <span
              v-if="step.shift.humanTravelTime"
              class="text-medium-emphasis"
            >
              · {{ t('routePlan.travelTime') }}: {{ step.shift.humanTravelTime }}
            </span>
          </v-list-item-subtitle>
        </v-list-item>
      </template>
    </v-list-group>

    <!-- Caregivers without assignments -->
    <v-list-item
      v-for="rpc in unassignedCaregivers"
      :key="`unassigned-${rpc.caregiverId}`"
      density="compact"
      class="text-medium-emphasis"
    >
      <template #prepend>
        <div class="mr-2 unassigned-dash" />
      </template>
      <v-list-item-title class="text-body-2">
        {{ rpc.caregiverFullName }}
      </v-list-item-title>
      <template #append>
        <v-chip
          size="x-small"
          color="grey"
          variant="tonal"
        >
          {{ t('routePlan.noAssignments') }}
        </v-chip>
      </template>
    </v-list-item>
  </v-list>
</template>

<style scoped>
.color-dot-md {
  width: 12px;
  height: 12px;
}

.unassigned-dash {
  width: 12px;
  height: 2px;
  background-color: #9e9e9e;
  border-radius: 1px;
}
</style>
